/**
 * Image intake for the panel (ADR-11, spec section 2.4): PNG or JPEG only, at most 10, longest edge
 * downscaled to 2560 in a canvas, and the 5 MB limit checked on the blob that will actually upload.
 */
import { ACCEPTED_IMAGE_TYPES, MAX_EDGE, MAX_IMAGE_BYTES, MAX_IMAGES } from './constants';
import type { DraftImage } from './types';

export const ERROR_WRONG_TYPE = 'Only PNG and JPEG images can be attached.';
export const errorOverCount = (skipped: number): string =>
  `You can attach up to ${MAX_IMAGES} images. ${skipped} ${skipped === 1 ? 'was' : 'were'} not added.`;
export const errorTooLarge = (name: string): string =>
  `${name} is larger than 5 MB after resizing and was not added.`;

type AcceptedType = (typeof ACCEPTED_IMAGE_TYPES)[number];
const isAcceptedType = (type: string): type is AcceptedType =>
  (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type);

export interface PlannedFiles {
  /** Files that passed the type and count checks and still need decoding. */
  files: File[];
  errors: string[];
}

/** Synchronous checks: type first, then the count against what is already attached. */
export function planFiles(files: File[], existingCount: number): PlannedFiles {
  const errors: string[] = [];
  const typed = files.filter((f) => isAcceptedType(f.type));
  if (typed.length < files.length) errors.push(ERROR_WRONG_TYPE);
  const room = Math.max(0, MAX_IMAGES - existingCount);
  const accepted = typed.slice(0, room);
  if (typed.length > accepted.length) errors.push(errorOverCount(typed.length - accepted.length));
  return { files: accepted, errors };
}

interface Decoded {
  width: number;
  height: number;
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
  close: () => void;
}

async function decode(file: File): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      draw: (ctx, w, h) => ctx.drawImage(bitmap, 0, 0, w, h),
      close: () => bitmap.close?.(),
    };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = url;
    });
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
      close: () => undefined,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toBlob(canvas: HTMLCanvasElement, type: AcceptedType): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, type === 'image/jpeg' ? 0.92 : undefined));
}

/** Downscales when the longest edge exceeds 2560; otherwise returns the file untouched. */
async function downscale(file: File, type: AcceptedType): Promise<Blob> {
  const decoded = await decode(file);
  try {
    const longest = Math.max(decoded.width, decoded.height);
    if (longest <= MAX_EDGE) return file;
    const scale = MAX_EDGE / longest;
    const width = Math.round(decoded.width * scale);
    const height = Math.round(decoded.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    decoded.draw(ctx, width, height);
    return (await toBlob(canvas, type)) ?? file;
  } finally {
    decoded.close();
  }
}

export type ProcessResult = { image: DraftImage } | { error: string };

/** Decodes, downscales and size-checks one accepted file. `label` is the name shown in the size error. */
export async function processImage(file: File, id: string, label: string): Promise<ProcessResult> {
  if (!isAcceptedType(file.type)) return { error: ERROR_WRONG_TYPE };
  let blob: Blob;
  try {
    blob = await downscale(file, file.type);
  } catch {
    return { error: ERROR_WRONG_TYPE };
  }
  if (blob.size > MAX_IMAGE_BYTES) return { error: errorTooLarge(label) };
  return {
    image: {
      id,
      blob,
      contentType: file.type,
      bytes: blob.size,
      objectUrl: URL.createObjectURL(blob),
      name: label,
    },
  };
}

export interface AddResult {
  accepted: DraftImage[];
  errors: string[];
}

/** Plans and processes a batch in one call (used where no placeholder UI is needed). */
export async function addImages(files: File[], existingCount: number): Promise<AddResult> {
  const plan = planFiles(files, existingCount);
  const errors = [...plan.errors];
  const accepted: DraftImage[] = [];
  for (const file of plan.files) {
    const result = await processImage(file, crypto.randomUUID(), file.name || 'Pasted image');
    if ('image' in result) accepted.push(result.image);
    else errors.push(result.error);
  }
  return { accepted, errors };
}

/** A skeleton entry shown while a file is decoded; null for a file that is not PNG or JPEG. */
export function placeholderImage(file: File, id: string, label: string): DraftImage | null {
  if (!isAcceptedType(file.type)) return null;
  return { id, blob: null, contentType: file.type, bytes: 0, objectUrl: null, name: label };
}
